/**
 * Confirm successful receipt of analysis results (Phase B: Quota Refund Protection)
 *
 * Extracted from useAnalyze.ts as part of E3 refactoring, S3.1 (#743).
 * This is a standalone async function with zero closure dependencies on the hook.
 *
 * This function is called AFTER the client successfully caches analysis results.
 * It marks the analysis as "confirmed received" in the quota ledger, preventing
 * automatic refunds by the background job.
 */

import { authenticatedFetch } from '@dramadetector/api-client';
import { getEdgeFunctionUrl, getGatewayHeaders } from '../../../../lib/api-url';

/**
 * Confirm successful receipt of analysis results.
 *
 * @param analysisId - The analysis ID to confirm
 * @param userId - The authenticated user ID (if authenticated)
 * @param deviceId - The device ID (if anonymous via Turnstile token)
 * @returns Promise<boolean> - True if confirmed, false if already confirmed
 */
export async function confirmAnalysisReceipt(
  analysisId: string,
  userId?: string,
  deviceId?: string
): Promise<boolean> {
  try {
    const response = await authenticatedFetch(getEdgeFunctionUrl('confirm-analysis-receipt'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getGatewayHeaders(),
        ...(deviceId ? { 'x-device-id': deviceId } : {}),
      },
      body: JSON.stringify({ analysisId, deviceId }),
      ...(userId ? {} : { skipAuth: true }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Confirmation failed');
    }

    const { data } = await response.json();
    return !data.alreadyConfirmed;
  } catch (error) {
    console.error('[confirmAnalysisReceipt] Error:', error);
    throw error;
  }
}
